/**
 * lib/teacherHr.ts
 * ------------------------------------------------------------------
 * A teacher's HR staff record, made from the teacher (Oct 2026).
 *
 * The academic side creates teachers (Setup → Teachers, reportTeachers);
 * HR, payroll and the clocking upload work only from the HR staff list
 * (hrStaff). Nothing joined the two after the June migration, so every
 * teacher added since arrived without an HR record — Chandra and Nyateka,
 * created 21 Sep 2026, were still missing from HR on 19 Oct. Now the person
 * who creates a teacher creates the HR record with them, linked by
 * `linkedTeacherId` and stored at `tch-<teacherId>` like the migrated ones
 * (functions/users.js keeps that record's email in step with the login).
 *
 * Only what the teacher record knows goes in: name, email, phone,
 * department, teaching category, the next teaching staff number, active.
 * Start date, residency, pay and the rest stay HR's to fill in.
 * firestore.rules lets the academic administrator create exactly this
 * shape and nothing else.
 * ------------------------------------------------------------------ */
import { nextStaffNumber, type StaffCategory } from '@/lib/hr';

export interface TeacherLike {
  id: string;
  initials?: string;
  lastName: string;
  email?: string;
  phone?: string;
  department?: string;
}

export interface StaffLike {
  id: string;
  employeeNo?: string;
  category: StaffCategory;
  startDate?: string;
  linkedTeacherId?: string;
}

/** The HR record id for a teacher — the form the migration used. */
export const hrIdForTeacher = (teacherId: string) => `tch-${teacherId}`;

/** True when some HR record already stands for this teacher. */
export function hasHrRecord(teacherId: string, staff: StaffLike[]): boolean {
  return staff.some((s) => s.linkedTeacherId === teacherId || s.id === hrIdForTeacher(teacherId));
}

/** The fields the academic side may put on a teacher's new HR record. */
export const TEACHER_HR_FIELDS = [
  'employeeNo', 'firstName', 'lastName', 'category', 'jobTitle', 'department', 'email', 'phone',
  'linkedTeacherId', 'status', 'startDate', 'createdAt', 'createdBy', 'source',
] as const;

export function hrRecordForTeacher(
  t: TeacherLike,
  staff: StaffLike[],
  by: string,
  now = new Date().toISOString(),
): { id: string; data: Record<string, string> } {
  const dept = t.department?.trim();
  const data: Record<string, string> = {
    employeeNo: nextStaffNumber('teaching', staff),
    // Only initials are on a teacher; HR puts the first name in.
    firstName: (t.initials || '').trim() || '—',
    lastName: t.lastName.trim(),
    category: 'teaching',
    jobTitle: dept ? `${dept} Teacher` : 'Teacher',
    linkedTeacherId: t.id,
    status: 'active',
    // Provisional — the day the record was made; HR sets the real start date.
    startDate: now,
    createdAt: now,
    createdBy: by,
    source: 'teacher-setup',
  };
  if (dept) data.department = dept;
  if (t.email?.trim()) data.email = t.email.trim();
  if (t.phone?.trim()) data.phone = t.phone.trim();
  return { id: hrIdForTeacher(t.id), data };
}
